import { useEffect, useRef, useCallback } from "react";

const SSO_MESSAGE_TYPE = "sso_silent_auth_result";
const DEFAULT_TIMEOUT = 3000;

// Attempts silent SSO authentication in a hidden iframe.
//
// OIDC/SAML silent flows rely on browser redirects and IdP session cookies,
// which require real navigation inside an iframe (XHR cannot be used).
// On success the page is reloaded so the server session reflects the
// newly authenticated user.
const useSilentSsoAuth = (silentAuthUrl, { timeout = DEFAULT_TIMEOUT } = {}) => {
  const iframeRef = useRef(null);
  const timeoutRef = useRef(null);

  const cleanup = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (iframeRef.current?.parentNode) {
      iframeRef.current.parentNode.removeChild(iframeRef.current);
      iframeRef.current = null;
    }
  }, []);

  const createHiddenIframe = useCallback(url => {
    const iframe = document.createElement("iframe");

    iframe.style.display = "none";
    iframe.setAttribute("data-silent-auth", "true");
    document.body.appendChild(iframe);

    iframeRef.current = iframe;
    iframe.src = url;
  }, []);

  const handleMessage = useCallback(
    event => {
      if (event.origin !== window.location.origin) return;

      const { type, success, error } = event.data || {};
      if (type !== SSO_MESSAGE_TYPE) return;

      cleanup();

      if (success) {
        window.location.reload();
      } else {
        console.debug("Silent SSO auth failed:", error);
      }
    },
    [cleanup]
  );

  useEffect(() => {
    if (!silentAuthUrl) return;

    window.addEventListener("message", handleMessage);
    createHiddenIframe(silentAuthUrl);
    timeoutRef.current = setTimeout(cleanup, timeout);

    return () => {
      cleanup();
      window.removeEventListener("message", handleMessage);
    };
  }, [silentAuthUrl, timeout, cleanup, createHiddenIframe, handleMessage]);
};

export default useSilentSsoAuth;
